import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Chatbot from '../components/Chatbot';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

export function NewLandingPage() {
  const [animationComplete, setAnimationComplete] = useState(false);
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('.scroll-animate');
    cards.forEach((card) => observer.observe(card));

    return () => {
      cards.forEach((card) => observer.unobserve(card));
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-black text-white">
      <Navbar isPublic={true} />
      
      {/* Hero Section with Animation - EXACT COPY from current home page */}
      <div className="px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-slate-900 via-blue-950 to-black" style={{ background: 'linear-gradient(167deg, #0f172a, #1a2c6c, #000000)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="sm:text-center md:max-w-3xl md:mx-auto lg:col-span-8 lg:text-left relative z-10">
              <h1>
                <div className="text-4xl tracking-tight font-extrabold sm:text-5xl xl:text-6xl">
                  <div className="flex flex-col md:items-start items-center">
                    <div className={`transition-all duration-1000 ease-out relative z-20 ${animationComplete ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
                      <span className="text-white ml-0">Document your</span>
                    </div>
                    <span className={`text-white md:ml-8 ml-4 transition-all duration-1000 delay-300 ease-out relative z-20 ${animationComplete ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
                      Salesforce Flows
                    </span>
                    <span className={`text-blue-600 text-7xl sm:text-8xl xl:text-9xl font-black mt-4 mb-6 lg:-ml-20 md:-ml-12 -ml-6 self-center md:self-auto transition-all duration-1000 delay-700 ease-out relative z-0 ${animationComplete ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{ textShadow: '0 0 240px rgba(0, 0, 0, 1), 0 0 160px rgba(0, 0, 0, 1), 0 0 400px rgba(0, 0, 0, 0.95), 0 0 600px rgba(0, 0, 0, 0.9), 0 0 800px rgba(0, 0, 0, 0.8)' }}>
                      Automatically!
                    </span>
                  </div>
                </div>
              </h1>
              <p className="mt-3 text-base text-gray-400 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl relative z-10">
                Generate comprehensive documentation for your Salesforce Flows with a single click. Summarize your flows in easy-to-understand language, highlight issues, and create cross-references that let you easily see which flows impact which fields!
              </p>
              <div className="mt-8 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left lg:mx-0 relative z-10">
                <Link to="/signup" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                  Start Your Free Trial
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4 relative">
              <div className={`absolute transition-all duration-1000 delay-1000 ease-out z-0 ${animationComplete ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'}`} style={{ bottom: '-136px', right: '-65px' }}>
                <img 
                  src="/screenshots/home/Banner_Dashboard.png" 
                  alt="FlowDocs Dashboard Preview" 
                  className="rounded-lg"
                  style={{ 
                    width: 'auto', 
                    height: 'auto',
                    transform: 'scale(1.2)',
                    transformOrigin: 'bottom right'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What's in your org? Section */}
      <section id="issue-detection" className="bg-white text-gray-900">
        <div className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-5xl md:text-6xl font-extrabold text-center mb-8 text-gray-900">
              What's in <span className="italic">your</span> Org?
            </h2>
            <p className="text-xl text-center text-gray-600 mb-16 max-w-4xl mx-auto">
              See which flows have critical issues, track error trends over time, and identify which users are most impacted. See the big picture instead of clicking through flows one by one.
            </p>
          </div>
        </div>
        
        {/* Full size dashboard image sitting on top of blue background */}
        <div className="relative">
          <div className="container mx-auto max-w-6xl relative z-10">
            <img 
              src="/screenshots/home/Dashboard Hero.png" 
              alt="Flowdocs Dashboard" 
              className="w-full h-auto"
              style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}
            />
          </div>
          
          {/* Three screenshot cards - full width background */}
          <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-black pt-16 pb-32 relative z-20" style={{ background: 'linear-gradient(167deg, #0f172a, #1a2c6c, #000000)' }}>
          <div className="container mx-auto max-w-6xl px-8 overflow-visible">
            {/* First row - Issue Distribution and Filter by Any Issue */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 overflow-visible">

            {/* Issue Distribution card - LEFT half */}
            <div className="relative">
              <img 
                src="/screenshots/home/Dashboard Issue Distribution widget 1.png" 
                alt="Issue Distribution" 
                className="w-full h-auto rounded-lg"
              />
              <div 
                id="issue-dist-caption"
                className={`scroll-animate absolute -left-12 top-1/2 -translate-y-1/2 bg-green-600 rounded-lg shadow-xl p-4 max-w-xs transition-all duration-1000 z-30 ${
                  visibleCards.has('issue-dist-caption') ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
                }`}
              >
                <h3 className="font-semibold text-sm mb-1 text-white">Issue Distribution</h3>
                <ul className="text-xs text-green-100 space-y-0.5">
                  <li>• Categorized problems</li>
                  <li>• Priority rankings</li>
                  <li>• Actionable insights</li>
                </ul>
              </div>
            </div>

            {/* Filter by Any Issue card - RIGHT half */}
            <div className="relative">
              <img 
                src="/screenshots/home/Queries in Loops 1.png" 
                alt="Queries in Loops Analysis" 
                className="w-full h-auto rounded-lg"
              />
              <div 
                id="filter-caption"
                className={`scroll-animate absolute -right-12 bg-green-600 rounded-lg shadow-xl p-4 transition-all duration-1000 delay-300 z-50 ${
                  visibleCards.has('filter-caption') ? 'translate-x-0 opacity-100 scale-100' : '-translate-x-48 opacity-0 scale-95'
                }`}
                style={{ 
                  top: 'calc(50% + 75px)', 
                  maxWidth: '400px',
                  zIndex: 60
                }}
              >
                <h3 className="font-semibold text-sm mb-1 text-white">Filter by Any Issue</h3>
                <ul className="text-xs text-green-100 space-y-0.5">
                  <li>• Clicking on any issue in the dashboard shows you a filtered list of flows impacted by it</li>
                  <li>• See each flow's score at a glance and click to view details</li>
                </ul>
              </div>
            </div>
            </div>

          </div>
        </div>
        </div>
        
        {/* Two Panes of Glass card - Breaking out of blue bar */}
        <div className="relative -mt-32 pb-24 bg-white">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="max-w-3xl mx-auto relative">
              <img 
                src="/screenshots/home/2 panes v3.jpg" 
                alt="Two Panes of Glass" 
                className="w-full h-auto rounded-lg relative z-20"
              />
              <div 
                id="two-panes-caption"
                className={`scroll-animate absolute -right-16 bottom-0 translate-y-9 bg-green-600 rounded-lg shadow-xl p-4 max-w-xs transition-all duration-1000 z-30 ${
                  visibleCards.has('two-panes-caption') ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
                }`}
              >
                <h3 className="font-semibold text-sm mb-1 text-white">Two Panes of Glass</h3>
                <ul className="text-xs text-green-100 space-y-0.5">
                  <li>• Open the flow in your org for editing with a direct link from FlowDocs</li>
                  <li>• See the issues you need to fix in one pane while you edit in the other</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation that's always on Section */}
      <section className="bg-white text-gray-900">
        <div id="automatic-documentation" className="pt-15 pb-16 px-4 scroll-mt-20">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-8">
              Documentation that's <span className="italic text-blue-600">Always On</span>
            </h2>
            
            <p className="text-xl text-center text-gray-600 mb-10 max-w-4xl mx-auto">
              No more wasting time and money on documentation that goes stale as fast as you write it!
              <br /><br />
              Never rewrite flow documentation because you don't trust what's on the wiki again.
              <br /><br />
              Rest easy knowing your team has the information they need to do their job faster and more efficiently.
              <br /><br />
              <span className="font-bold italic">Every. Single. Day.</span>
              <br /><br />
              Get plain language descriptions of the purpose of each flow:
              <br />
              A summary of the flow's purpose, and a step-by-step walkthrough of key points.
              <br /><br />
              Documentation designed for business users, but detailed enough that developers will appreciate it.
            </p>
          </div>
        </div>

        {/* Full size summary image sitting on top of blue background */}
        <div className="relative">
          <div className="container mx-auto max-w-6xl px-4 relative">
            <img 
              src="/screenshots/home/Summary Hero.png" 
              alt="Flow Summary Documentation" 
              className="w-full h-auto rounded-t-lg relative z-10"
              style={{ boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.3), -10px 0 40px rgba(0, 0, 0, 0.2), 10px 0 40px rgba(0, 0, 0, 0.2)' }}
            />
          </div>

          {/* Two column layout with screenshots - full width blue background */}
          <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-black py-16 relative z-20" style={{ background: 'linear-gradient(167deg, #0f172a, #1a2c6c, #000000)' }}>
          <div className="container mx-auto max-w-6xl px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            {/* Left column - Flow Health Grades */}
            <div className="flex flex-col">
              <h3 className="text-2xl font-bold mb-4 text-white">
                See your flow health at a glance
              </h3>
              
              <div className="bg-white rounded-lg shadow-xl overflow-hidden flex-1 flex flex-col">
                <img 
                  src="/screenshots/home/Flows - List View with Grades 1.png" 
                  alt="Flows List View" 
                  className="w-full h-auto"
                />
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-semibold text-lg mb-2">Flow Health Grades</h3>
                  <ul className="text-sm text-gray-600 space-y-1 flex-1">
                    <li>• Flows grades are scored whenever they are synced</li>
                    <li>• A through D grades, plus "I" for incomplete</li>
                    <li>• Filter by Grades, Flow Type, Issues, or Keywords</li>
                    <li>• Sort by Scores to rank best-to-worst or worst-to-best</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right column - Flow Scorecards */}
            <div id="flow-health" className="scroll-mt-20 flex flex-col">
              <h3 className="text-2xl font-bold mb-4 text-white">
                Scorecards instantly highlight issues
              </h3>
              
              <div className="bg-white rounded-lg shadow-xl overflow-hidden flex-1 flex flex-col">
                <img 
                  src="/screenshots/home/Query in Loop Flow Scorecard 1.png" 
                  alt="Flow Scorecard" 
                  className="w-full h-auto"
                />
                <div className="px-6 pb-6 pt-0.5 flex-1 flex flex-col">
                  <h3 className="font-semibold text-lg mb-2">Performance Scorecards</h3>
                  <ul className="text-sm text-gray-600 space-y-1 flex-1">
                    <li>• Uses rubrics to score flows based on common best practices</li>
                    <li>• Assesses Query Risk, DML Risk, Bulk Safety Risk, and CPU Risk</li>
                    <li>• Gives a brief metrics-based overview</li>
                    <li>• Briefly explains issues with links to select help articles</li>
                    <li>• Recommends steps to remediate issues</li>
                    <li>• Provides specific, detailed grading calculations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        </div>

        {/* Additional documentation benefits */}
        <div id="field-impact" className="px-4 py-16 scroll-mt-24">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-8 text-gray-900">
              Immediately see what <span className="text-blue-600 italic">Flows</span> touch which <span className="text-blue-600 italic">Fields</span>
            </h2>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <img 
                    src="/screenshots/home/Fields - Field Impact - Account.png" 
                    alt="Field Impact Analysis" 
                    className="w-full h-auto rounded-lg"
                  />
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-3">See <span className="text-blue-600 font-bold">ALL</span> the fields used in flows, and <span className="italic">how</span>...</h4>
                  <p className="text-gray-600 mb-4">
                    Trace flows that are impacting data in specific fields, or quickly understand the ripple effect of planned changes across your automations.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• See flows organized by the field they touch</li>
                    <li>• Quickly filter by object, field, or keyword</li>
                    <li>• Quickly see how fields are used: read, update, create, filter, or condition</li>
                    <li>• Identify the impact changes to fields may have across your org</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* That's why Flowdocs is perfect for... Section */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-16 text-gray-900">
            That's why FlowDocs is <span className="italic text-blue-600">perfect</span> for...
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Salesforce Admins, Architects & Consultants</h3>
              <p className="text-lg text-blue-600 mb-6">
                Any organization that wants to keep their documentation accurate at all times will benefit from this application.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <ChevronRightIcon className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Automatic documentation keeps everyone on the same page</span>
                </li>
                <li className="flex items-start">
                  <ChevronRightIcon className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Find issues before they cause problems in production</span>
                </li>
                <li className="flex items-start">
                  <ChevronRightIcon className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Ensure compliance with Salesforce best practices</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Teams managing complex orgs</h3>
              <p className="text-lg text-blue-600 mb-6">
                Large enterprise orgs with hundreds or thousands of flows need automated documentation to maintain control.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <ChevronRightIcon className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Track changes across the entire org automatically</span>
                </li>
                <li className="flex items-start">
                  <ChevronRightIcon className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Identify technical debt and prioritize improvements</span>
                </li>
                <li className="flex items-start">
                  <ChevronRightIcon className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Onboard new team members faster with complete org visibility</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-10 py-4 text-xl font-semibold rounded-lg bg-green-600 hover:bg-green-700 transition-colors text-white"
            >
              Get Started - Free Trial
              <ChevronRightIcon className="ml-2 h-6 w-6" />
            </Link>
          </div>
        </div>
      </section>

      
      <Chatbot />
    </div>
  );
}